/**
 * Il calcolo dell'assistente (D1 del piano in docs/sviluppi/assistente-campo).
 *
 * Il modello non fa conti a mente: quando gli serve una somma, una media, una
 * variazione o una tendenza chiama `calcola` con i valori già letti, e il
 * conto lo fa questa funzione. Le operazioni sono chiuse e il risultato porta
 * una descrizione leggibile, così sotto la risposta si vede che conto è stato
 * fatto. Che i valori vengano davvero dai dati letti lo controlla il server
 * (D2), non questo modulo.
 */

import { z } from 'zod';

export const OPERAZIONI_CALCOLO = [
  'somma',
  'media',
  'mediana',
  'minimo',
  'massimo',
  'conteggio',
  'differenza',
  'variazione',
  'percentuale',
  'rapporto',
  'classifica',
  'tendenza',
] as const;
export type OperazioneCalcolo = (typeof OPERAZIONI_CALCOLO)[number];

/** Le operazioni che vogliono esattamente due valori, nell'ordine a, b. */
const A_DUE_TERMINI: readonly OperazioneCalcolo[] = [
  'differenza',
  'variazione',
  'percentuale',
  'rapporto',
];

export const MAX_VALORI_CALCOLO = 500;

/** Sotto questa quota della media, sull'intera serie, la tendenza è stabile. */
const SOGLIA_STABILE = 0.05;

export const calcoloSchema = z
  .object({
    operazione: z
      .enum(OPERAZIONI_CALCOLO)
      .describe(
        'somma, media, mediana, minimo, massimo, conteggio; differenza (b − a), variazione ' +
          '(da a a b, in percentuale), percentuale (a su b), rapporto (a / b) vogliono due ' +
          'valori nell’ordine a, b; classifica ordina le voci; tendenza (almeno tre valori in ' +
          'ordine di tempo) dà la pendenza per passo e la direzione.',
      ),
    valori: z
      .array(
        z.object({
          etichetta: z.string().max(200).describe('Di chi o di che cosa è il valore.'),
          valore: z.number().finite().describe('Un numero letto dai dati, così com’è.'),
        }),
      )
      .min(1, 'Serve almeno un valore.')
      .max(MAX_VALORI_CALCOLO, `Al massimo ${MAX_VALORI_CALCOLO} valori.`),
    ordine: z
      .enum(['decrescente', 'crescente'])
      .optional()
      .describe('Per la classifica; di norma decrescente.'),
    decimali: z
      .number()
      .int()
      .min(0)
      .max(3)
      .optional()
      .describe('Cifre decimali del risultato, di norma 1.'),
  })
  .superRefine((r, ctx) => {
    if (A_DUE_TERMINI.includes(r.operazione) && r.valori.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valori'],
        message: `${r.operazione} vuole esattamente due valori, nell’ordine a, b.`,
      });
    }
    if (r.operazione === 'tendenza' && r.valori.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valori'],
        message: 'La tendenza vuole almeno tre valori.',
      });
    }
  });

export type RichiestaCalcolo = z.input<typeof calcoloSchema>;

export interface VoceClassifica {
  posizione: number;
  etichetta: string;
  valore: number;
}

export interface EsitoCalcolo {
  operazione: OperazioneCalcolo;
  descrizione: string;
  /** Assente solo nella classifica. */
  risultato?: number;
  /** Per minimo e massimo: di chi è il valore, pari merito compresi. */
  voci?: string[];
  classifica?: VoceClassifica[];
  /** Per la tendenza: pendenza × passi, cioè quanto cambia sull'intera serie. */
  variazioneStimata?: number;
  direzione?: 'in crescita' | 'stabile' | 'in calo';
}

/** Arrotonda a metà verso l'alto, senza gli errori della virgola mobile (1,005 → 1,01). */
export function arrotonda(x: number, decimali: number): number {
  const esatto = Number(`${Math.round(Number(`${x}e${decimali}`))}e-${decimali}`);
  if (Number.isFinite(esatto)) return esatto;
  const f = 10 ** decimali;
  return Math.round(x * f) / f;
}

const it = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 3 });
const conSegno = (n: number) => `${n > 0 ? '+' : ''}${it(n)}`;

const somma = (xs: readonly number[]) => xs.reduce((s, x) => s + x, 0);

function mediana(xs: readonly number[]): number {
  const o = [...xs].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m]! : (o[m - 1]! + o[m]!) / 2;
}

/** Pendenza della retta dei minimi quadrati sui punti (0, x0), (1, x1), … */
function pendenza(xs: readonly number[]): number {
  const n = xs.length;
  const mx = (n - 1) / 2;
  const my = somma(xs) / n;
  let num = 0;
  let den = 0;
  xs.forEach((y, i) => {
    num += (i - mx) * (y - my);
    den += (i - mx) ** 2;
  });
  return num / den;
}

function nonZero(x: number, messaggio: string): void {
  if (x === 0) throw new RangeError(messaggio);
}

/**
 * Esegue un calcolo su valori già validati da `calcoloSchema`. Una divisione
 * per zero è un RangeError con un messaggio da riferire all'utente.
 */
export function calcola(richiesta: RichiestaCalcolo): EsitoCalcolo {
  const { operazione, valori, ordine = 'decrescente', decimali = 1 } = richiesta;
  const xs = valori.map((v) => v.valore);
  const n = xs.length;
  const r = (x: number) => arrotonda(x, decimali);
  const [a, b] = xs as [number, number];

  switch (operazione) {
    case 'somma':
      return { operazione, risultato: r(somma(xs)), descrizione: `somma di ${n} valori` };
    case 'media':
      return { operazione, risultato: r(somma(xs) / n), descrizione: `media di ${n} valori` };
    case 'mediana':
      return { operazione, risultato: r(mediana(xs)), descrizione: `mediana di ${n} valori` };
    case 'conteggio':
      return { operazione, risultato: n, descrizione: `${n} valori` };
    case 'minimo':
    case 'massimo': {
      const estremo = operazione === 'minimo' ? Math.min(...xs) : Math.max(...xs);
      const voci = valori.filter((v) => v.valore === estremo).map((v) => v.etichetta);
      return {
        operazione,
        risultato: estremo,
        voci,
        descrizione: `${operazione} di ${n} valori: ${voci.join(', ')}`,
      };
    }
    case 'differenza':
      return { operazione, risultato: r(b - a), descrizione: `${it(b)} meno ${it(a)}` };
    case 'variazione': {
      nonZero(a, 'La variazione in percentuale non si calcola se si parte da zero.');
      const risultato = r(((b - a) / Math.abs(a)) * 100);
      return {
        operazione,
        risultato,
        descrizione: `da ${it(a)} a ${it(b)}: ${conSegno(risultato)}%`,
      };
    }
    case 'percentuale': {
      nonZero(b, 'La percentuale non si calcola su un totale uguale a zero.');
      return { operazione, risultato: r((a / b) * 100), descrizione: `${it(a)} su ${it(b)}` };
    }
    case 'rapporto': {
      nonZero(b, 'Il rapporto non si calcola con un divisore uguale a zero.');
      return { operazione, risultato: r(a / b), descrizione: `${it(a)} diviso ${it(b)}` };
    }
    case 'classifica': {
      const segno = ordine === 'crescente' ? 1 : -1;
      const ordinati = [...valori].sort((x, y) => segno * (x.valore - y.valore));
      const classifica = ordinati.map((v) => ({
        posizione: ordinati.findIndex((w) => w.valore === v.valore) + 1,
        etichetta: v.etichetta,
        valore: v.valore,
      }));
      return {
        operazione,
        classifica,
        descrizione: `classifica di ${n} voci, dal più ${ordine === 'crescente' ? 'basso' : 'alto'}`,
      };
    }
    case 'tendenza': {
      const p = pendenza(xs);
      const totale = p * (n - 1);
      const media = Math.abs(somma(xs) / n);
      const stabile = media === 0 ? totale === 0 : Math.abs(totale) < SOGLIA_STABILE * media;
      const direzione = stabile ? 'stabile' : totale > 0 ? 'in crescita' : 'in calo';
      return {
        operazione,
        risultato: r(p),
        variazioneStimata: r(totale),
        direzione,
        descrizione: `tendenza su ${n} valori: ${direzione}, ${conSegno(r(p))} per passo`,
      };
    }
  }
}
